// A co-written book's byline (W9c; PROTOCOL.md §10.13): the co-authors after the project's own published name, kept by
// value in the project document as JSON `settings.byline` (`{"names": [...], "joiner": "and"}`, and a musical's
// `"roles": {name: ["book", "music", "lyrics"]}`, the owner's under "") through the
// settings-JSON rules, like the pen name, so every device compiles the same byline. Only the owner edits it (the
// apps enforce that). Compile reads it into `AuthorInfo.coAuthors`.
import * as A from '@automerge/automerge';
import { SwiftReader } from '../automerge/swiftDoc.ts';
import { str, Writer } from '../edit/typed.ts';
import { decodeJSON, lossyArray, mergedJSON } from '../settingsJSON.ts';
import { trimCFWhitespace } from '../unicode.ts';

export interface Byline {
  /** Co-authors' names, in byline order (the project's own name comes first and isn't listed). */
  names: string[];
  /** The word before the last name ("and"). */
  joiner: string;
  /** A musical's credits (`Byline.roles`): each name's roles (raw `CreditRole` values; unknown ones kept), the project's
   *  own author under `OWN_AUTHOR`. Absent or empty: no roles, and not stored. */
  roles?: Record<string, string[]>;
}

/** `Byline.CreditRole`, in title-page order. */
export type CreditRole = 'book' | 'music' | 'lyrics';
export const CREDIT_ROLES: readonly CreditRole[] = ['book', 'music', 'lyrics'];
const CREDIT_NAMES: Record<CreditRole, string> = { book: 'Book', music: 'Music', lyrics: 'Lyrics' };
export const creditRoleName = (r: CreditRole): string => CREDIT_NAMES[r];

/** `Byline.ownAuthor`: the key the project's own author's roles are kept under. */
export const OWN_AUTHOR = '';

export const emptyByline = (): Byline => ({ names: [], joiner: 'and' });

/** `Byline(from:)`: names that aren't strings, or are blank, are dropped; a missing joiner is "and"; roles that can't be
 *  read are none (Swift decodes the map whole). */
export function decodeByline(json: unknown): Byline {
  if (!json || typeof json !== 'object' || Array.isArray(json)) throw new Error('byline');
  const o = json as Record<string, unknown>;
  const names = lossyArray(o.names, (v) => (typeof v === 'string' && trimCFWhitespace(v) !== '' ? trimCFWhitespace(v) : null));
  const joiner = typeof o.joiner === 'string' && trimCFWhitespace(o.joiner) !== '' ? trimCFWhitespace(o.joiner) : 'and';
  const roles = decodeRoles(o.roles);
  return Object.keys(roles).length ? { names, joiner, roles } : { names, joiner };
}

function decodeRoles(json: unknown): Record<string, string[]> {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return {};
  const out: Record<string, string[]> = {};
  for (const [name, value] of Object.entries(json as Record<string, unknown>)) {
    if (!Array.isArray(value) || !value.every((v) => typeof v === 'string')) return {};
    if (value.length) out[name] = value as string[];
  }
  return out;
}

/** `Byline.roles(of:)`: a person's roles (`OWN_AUTHOR` for the project's own author), in `CREDIT_ROLES` order. */
export function rolesOf(byline: Byline, name: string): CreditRole[] {
  const raw = new Set(byline.roles?.[name] ?? []);
  return CREDIT_ROLES.filter((r) => raw.has(r));
}

/** `Byline.setRoles(_:of:)`: a copy with a person's roles set, keeping any this build doesn't know. */
export function withRoles(byline: Byline, chosen: CreditRole[], name: string): Byline {
  const known = new Set<string>(CREDIT_ROLES);
  const kept = (byline.roles?.[name] ?? []).filter((r) => !known.has(r));
  const all = [...chosen, ...kept];
  const roles = { ...(byline.roles ?? {}) };
  if (all.length) roles[name] = all; else delete roles[name];
  return { ...byline, roles };
}

/** `Byline.creditLines(ownName:)`: "Book by A", "Music and Lyrics by B", "Music by B and C", in role order. Null when
 *  nobody has a role. */
export function creditLines(byline: Byline, ownName: string): string[] | null {
  const people: [string, string][] = [[OWN_AUTHOR, ownName], ...byline.names.map((n): [string, string] => [n, n])];
  if (!people.some(([key]) => rolesOf(byline, key).length)) return null;
  const lines: { roles: CreditRole[]; names: string[] }[] = [];
  for (const [key, shown] of people) {
    const r = rolesOf(byline, key);
    if (!r.length) continue;
    const same = lines.find((l) => l.roles.join() === r.join());
    if (same) same.names.push(shown); else lines.push({ roles: r, names: [shown] });
  }
  const rank = (l: { roles: CreditRole[] }) => (l.roles.length ? CREDIT_ROLES.indexOf(l.roles[0]) : 9);
  lines.sort((a, b) => rank(a) - rank(b));
  const list = (items: string[], joiner: string) =>
    items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} ${joiner} ${items[items.length - 1]}`;
  return lines.map((l) => `${list(l.roles.map(creditRoleName), 'and')} by ${list(l.names, byline.joiner)}`);
}

/** The project's byline from its settings (none: no co-authors). */
export function readByline(settings: Record<string, unknown>): Byline {
  return decodeJSON(settings.byline, decodeByline) ?? emptyByline();
}

/** The byline's stored string (unknown keys kept; throws, changing nothing, over a value this build can't read). An
 *  empty byline removes it (""), so the project compiles as a single author's. */
export function bylineJSON(stored: unknown, byline: Byline): string {
  const names = byline.names.map(trimCFWhitespace).filter((n) => n !== '');
  // Roles only for the people in it (and the project's own author); written only when there are some.
  const roles = Object.fromEntries(Object.entries(byline.roles ?? {})
    .filter(([name, r]) => r.length && (name === OWN_AUTHOR || names.includes(name))));
  const clean: Byline = { names, joiner: trimCFWhitespace(byline.joiner) || 'and', ...(Object.keys(roles).length ? { roles } : {}) };
  return mergedJSON(stored, names.length || clean.roles ? clean : null, decodeByline, 'byline');
}

/** `setByline` as one change to the project document ("Byline"); the same document when nothing changed. */
export function setByline(doc: A.Doc<unknown>, byline: Byline, now: Date): A.Doc<unknown> {
  const r = new SwiftReader(doc);
  const settings = r.map('_root', 'settings');
  const stored = settings ? (r.string(settings, 'byline') ?? '') : '';
  const json = bylineJSON(stored, byline);
  if (json === stored) return doc;
  return A.change(doc as A.Doc<Record<string, any>>, { message: 'Byline', time: Math.floor(now.getTime() / 1000) }, (d) => {
    const w = new Writer(d, r);
    if (json === '') w.delete(['settings'], 'byline');
    else w.put(['settings'], 'byline', str(json));
  }) as A.Doc<unknown>;
}
